import logger from '../utils/logger';
// smartreader-frontend/src/contexts/TagEventsContext.js
import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import {
  connectTagEventsSocket,
  disconnectTagEventsSocket,
  subscribeToAllDevices,
  subscribeToDevices,
  unsubscribeFromAll,
} from '../services/tag-events-socket';

const MAX_EVENTS = 500; // Limit in-memory events to prevent memory issues

const TagEventsContext = createContext({
  tagEvents: [],
  isConnected: false,
  connectionError: null,
  totalReceived: 0,
  clearEvents: () => {},
  subscribeToDevice: () => {},
  subscribeAll: () => {},
  unsubscribeAll: () => {},
});

export const TagEventsProvider = ({ children, autoSubscribeAll = true }) => {
  const [tagEvents, setTagEvents] = useState([]);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState(null);
  const totalReceivedRef = useRef(0);
  const [totalReceived, setTotalReceived] = useState(0);

  const handleTagEvent = useCallback((event) => {
    setTagEvents((prev) => {
      const newEvents = [event, ...prev];
      // Limit stored events to prevent memory issues
      return newEvents.slice(0, MAX_EVENTS);
    });
    totalReceivedRef.current += 1;
    setTotalReceived(totalReceivedRef.current);
  }, []);

  const handleConnectionChange = useCallback(({ connected, error, reason, socketId }) => {
    setIsConnected(connected);
    if (error) {
      setConnectionError(error);
    } else if (reason && reason !== 'io client disconnect') {
      setConnectionError(`Disconnected: ${reason}`);
    } else if (connected) {
      setConnectionError(null);
    }
  }, []);

  useEffect(() => {
    const socket = connectTagEventsSocket(handleTagEvent, handleConnectionChange);

    // Auto-subscribe to all devices on connection if enabled
    if (autoSubscribeAll) {
      socket.on('connect', () => {
        subscribeToAllDevices().catch((err) => {
          logger.error('[TagEvents] Auto-subscribe failed:', err);
        });
      });
    }

    return () => {
      unsubscribeFromAll();
      disconnectTagEventsSocket();
    };
  }, [handleTagEvent, handleConnectionChange, autoSubscribeAll]);

  const clearEvents = useCallback(() => {
    setTagEvents([]);
    totalReceivedRef.current = 0;
    setTotalReceived(0);
  }, []);

  const subscribeToDevice = useCallback(async (deviceSerials) => {
    const serials = Array.isArray(deviceSerials) ? deviceSerials : [deviceSerials];
    try {
      await subscribeToDevices(serials);
    } catch (err) {
      logger.error('[TagEvents] Subscribe failed:', err);
    }
  }, []);

  const subscribeAll = useCallback(async () => {
    try {
      await subscribeToAllDevices();
    } catch (err) {
      logger.error('[TagEvents] Subscribe all failed:', err);
    }
  }, []);

  const unsubscribeAllDevices = useCallback(() => {
    unsubscribeFromAll();
  }, []);

  return (
    <TagEventsContext.Provider
      value={{
        tagEvents,
        isConnected,
        connectionError,
        totalReceived,
        clearEvents,
        subscribeToDevice,
        subscribeAll,
        unsubscribeAll: unsubscribeAllDevices,
      }}
    >
      {children}
    </TagEventsContext.Provider>
  );
};

export const useTagEvents = () => {
  const context = useContext(TagEventsContext);
  if (!context) {
    throw new Error('useTagEvents must be used within a TagEventsProvider');
  }
  return context;
};

export default TagEventsContext;
